/* eslint-disable max-lines */
import { createRef, useCallback, useState } from 'react';
import {
  QuestionCircle,
  ChevronDown,
  PersonLinesFill,
  PersonVcard,
  BoxArrowInRight,
  Search,
  List,
  XLg,
  CircleHalf
} from 'react-bootstrap-icons';
import { useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import { Avatar, Button, InputRef, MenuProps, Space, Typography } from 'antd';
import { Header as AntdHeader } from 'antd/es/layout/layout';

import default_avatar from '@/assets/images/default_avatar.jpg';
import logoMobile from '@/assets/images/logo_mobile.svg';
import Dropdown from '@/components/shared/Dropdown';
import Logo from '@/components/shared/Logo';
import SearchInput from '@/components/shared/SearchInput';
import { getCdnUrl, truncateText } from '@/helpers';
import { useAppSelector } from '@/hooks/redux';
import { enUserType } from '@/interfaces/user';
import { toggleDrawer } from '@/redux/drawer/slice';
import { toggleTheme } from '@/redux/theme/slice';
import { loginAccounts } from '@/services/accounts';
import * as tokenService from '@/services/token';
import { getUserInfo } from '@/services/token';
import { useGetUserQuery } from '@/services/user';
import { V3_URL } from '@/settings';
import styled, { StyledProp } from '@/styled';

import Notifications from './Notifications';
import SearchModal from './SearchModal';

const makeLogout = () => {
  tokenService.logout('');
};

let items: MenuProps['items'] = [
  {
    key: '1',
    label: (
      <a target='_blank' rel='noopener noreferrer' href={V3_URL}>
        Visualizar como produtor
      </a>
    ),
    icon: <PersonLinesFill />
  },
  {
    key: '2',
    label: <Link to='/perfil'>Perfil</Link>,
    icon: <PersonVcard />
  },
  {
    key: '3',
    label: 'Sair',
    icon: <BoxArrowInRight />,
    onClick: makeLogout
  }
];

interface HeaderProps extends StyledProp {}

const Header = ({ className }: HeaderProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data: user, isFetching } = useGetUserQuery();
  const isDrawerOpened = useAppSelector(state => state.drawer.opened);
  const [isSearchOpen, setSearchOpen] = useState(false);

  const inputSearchRef = createRef<InputRef>();

  const handleNavigate = useCallback((path: string) => (user ? navigate(path) : loginAccounts()), [navigate, user]);

  const handlePressedAccessKey = useCallback(
    (open: boolean) => {
      setSearchOpen(open);
      inputSearchRef?.current?.blur();
    },
    [inputSearchRef]
  );

  const closeModal = useCallback(() => {
    setSearchOpen(false);
    inputSearchRef?.current?.blur();
  }, [inputSearchRef]);

  const handleToggleDrawer = () => dispatch(toggleDrawer());
  if (user?.type === enUserType.learner) items = items.filter(item => item.key !== '1');

  const handleChangeTheme = () => dispatch(toggleTheme());
  const userInfo = getUserInfo();

  return (
    <AntdHeader className={className}>
      <div className='header-left'>
        {user && (
          <Button type='ghost' className='searchButton' onClick={handleToggleDrawer}>
            {!isDrawerOpened && <List size={24} />}
            {isDrawerOpened && <XLg size={24} />}
          </Button>
        )}

        <div className='header-logo' onClick={() => handleNavigate('/resumo')}>
          <Logo className='nutrorLogo' />
          <img src={logoMobile} className='nutrorLogoMobile' />
          <div className='header-logo-experience'>Experience</div>
        </div>
      </div>

      {user && (
        <div className='header-center'>
          <div className='input'>
            <SearchInput ref={inputSearchRef} readOnly onClick={() => setSearchOpen(true)} />
          </div>

          <SearchModal handlePressedAccessKey={handlePressedAccessKey} closeModal={closeModal} open={isSearchOpen} />
        </div>
      )}

      <div className='header-right'>
        {user && (
          <Button type='ghost' className='searchButton'>
            <Search onClick={() => setSearchOpen(true)} />
          </Button>
        )}

        <Button type='ghost' onClick={handleChangeTheme}>
          <CircleHalf />
        </Button>

        {user && (
          <>
            <Notifications />

            <Button type='ghost'>
              <QuestionCircle onClick={() => handleNavigate('/ajuda')} />
            </Button>

            <div className='divider' />

            <Dropdown menu={{ items }} trigger={['click']} placement='bottomLeft'>
              <div className='dropdownContent'>
                <Space>
                  <Avatar.Group maxCount={2} className='avatars'>
                    <Avatar src={getCdnUrl(user?.avatar, default_avatar)} size={32} />
                    {userInfo?.tenant_id && (
                      <Avatar src={getCdnUrl(userInfo?.tenant_avatar, default_avatar)} size={32} />
                    )}
                  </Avatar.Group>

                  <Typography.Title level={5} className='dropdownText'>
                    {!isFetching && truncateText(userInfo?.tenant_name ?? user?.name, 20)} <ChevronDown />
                  </Typography.Title>
                </Space>
              </div>
            </Dropdown>
          </>
        )}
      </div>
    </AntdHeader>
  );
};

export default styled(Header)`
  position: fixed;
  z-index: 5;
  width: 100%;
  background-color: ${({ theme }) => theme.colorBgLayout};
  padding-inline: 0;
  height: ${({ theme }) => theme.variables.headerHeight}px;
  padding: ${({ theme }) => theme.space.spacing(0, 4)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: ${({ theme }) => theme.motionDurationMid};

  .avatars {
    padding-top: ${({ theme }) => theme.space.pxToRem(24)};
  }

  button {
    display: flex;
    color: ${({ theme }) => theme.colorTextSecondary};
    align-items: center;
    height: auto;
    border-radius: 100%;
    padding: 0;

    svg {
      font-size: 20px;
      display: block;
    }
  }

  .header-left {
    display: flex;
    gap: 8px;

    .header-logo {
      gap: 16px;
      display: flex;
      align-items: center;
      cursor: pointer;

      svg {
        width: 120px;
      }

      .header-logo-experience {
        font-weight: 300;
        font-size: ${({ theme }) => theme.space.pxToRem(12)};
        letter-spacing: 0.2em;
        text-transform: uppercase;
        border: 1px solid ${({ theme }) => theme.palette.gray[8]};
        line-height: 1;
        padding: 4px 16px;
        color: ${({ theme }) => theme.colorTextSecondary};

        ${({ theme }) => theme.breakpoints.down('xlg')} {
          display: none;
        }
      }
    }
  }

  .input {
    > span {
      border: 0;
      box-shadow: none;
    }
  }

  .dropdownContent {
    display: flex;
    align-items: center;
    height: 100%;
  }

  .nutrorLogoMobile {
    display: none;
  }

  .header-right {
    gap: 24px;
    display: flex;
    align-items: center;
  }

  .dropdownText {
    color: ${({ theme }) => theme.colorText};
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    user-select: none;
    cursor: pointer;

    h5.ant-typography {
      font-size: ${({ theme }) => theme.space.pxToRem(16)};
    }
  }

  .divider {
    border-left: 1px solid ${({ theme }) => theme.colorTextQuaternary};
    height: 22px;
  }

  .searchButton {
    display: none;
  }

  ${({ theme }) => theme.breakpoints.down('xlg')} {
    ${({ theme }) => theme.breakpoints.down('lg')} {
      .input {
        width: auto;

        span {
          width: auto;
        }

        input {
          width: auto;
        }
      }
    }

    ${({ theme }) => theme.breakpoints.down('lg')} {
      .nutrorLogo {
        display: none;
      }

      .nutrorLogoMobile {
        display: inline;
      }
    }

    ${({ theme }) => theme.breakpoints.down('md')} {
      .buttonsSpace {
        gap: 16px;
      }

      .searchButton {
        display: flex;
      }

      .logoSpace {
        width: 100%;
        gap: 0;
        margin-left: ${({ theme }) => theme.space.spacing(2)};
      }

      .input {
        display: none;
      }

      .dropdownText {
        display: none;
      }
    }
  }
`;
